import { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  isRouteErrorResponse,
  Navigate,
  Outlet,
  useRouteError,
} from 'react-router-dom';
import { AlertOctagon, Loader2, RotateCcw } from 'lucide-react';
import { AuthProvider } from '@/features/auth/AuthProvider';
import { AppShell } from '@/components/layout/AppShell';
import { ErrorBoundary } from '@/components/layout/ErrorBoundary';
import { Button, Empty } from '@shining-technologies/ui';

import { dashboardRoutes } from '@/routes/dashboard/routes';
import { analyticsRoutes } from '@/routes/analytics/routes';
import { workspaceRoutes } from '@/routes/workspaces/routes';
import { floorRoutes } from '@/routes/floors/routes';
import { userRoutes } from '@/routes/users/routes';
import { roleRoutes } from '@/routes/roles/routes';
import { projectRoutes } from '@/routes/projects/routes';
import { roomRoutes } from '@/routes/rooms/routes';
import { meetingRoutes } from '@/routes/meetings/routes';
import { recordingRoutes } from '@/routes/recordings/routes';
import { taskRoutes } from '@/routes/tasks/routes';
import { integrationRoutes } from '@/routes/integrations/routes';
import { providerRoutes } from '@/routes/providers/routes';
import { billingRoutes } from '@/routes/billing/routes';
import { auditRoutes } from '@/routes/audit/routes';
import { attendanceRoutes } from '@/routes/attendance/routes';
import { clientPolicyRoutes } from '@/routes/clientPolicy/routes';
import { securityRoutes } from '@/routes/security/routes';
import { errorRoutes } from '@/routes/errors/routes';
import { notificationRoutes } from '@/routes/notifications/routes';
import { filesRoutes } from '@/routes/files/routes';
import { releaseRoutes } from '@/routes/releases/routes';
import { systemRoutes } from '@/routes/system/routes';
import { messagingRoutes } from '@/routes/messaging/routes';
import { docsRoutes } from '@/routes/docs/routes';
import { settingsRoutes } from '@/routes/settings/routes';
import { agentRoutes } from '@/routes/agents/routes';
import { aiOversightRoutes } from '@/routes/aiOversight/routes';

const LoginPage = lazy(() => import('@/routes/auth/LoginPage'));

// AuthProvider needs router context (useNavigate), so it wraps the Outlet here.
function Root() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

function NotFound() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <Empty
        title="Page not found"
        description="The page you're looking for doesn't exist."
        actions={
          <Button asChild variant="outline">
            <a href="/dashboard">Back to dashboard</a>
          </Button>
        }
      />
    </div>
  );
}

/**
 * FIX(admin-route-error-element): the root errorElement rendered "Page not
 * found" for EVERY routing error — a crashed loader or a failed lazy chunk
 * (e.g. after a deploy) told the admin the page didn't exist. Before:
 * `<ErrorBoundary><NotFound /></ErrorBoundary>`. After: a 404 route response
 * still shows NotFound; anything else says something went wrong, shows the
 * message and offers a reload.
 */
function RouteError() {
  const error = useRouteError();
  if (isRouteErrorResponse(error) && error.status === 404) return <NotFound />;

  const message = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`.trim()
    : error instanceof Error
      ? error.message
      : null;

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-8">
      <Empty
        status="error"
        icon={<AlertOctagon aria-hidden />}
        title="Something went wrong"
        description={message || 'This page failed to load. Reloading usually fixes it.'}
        actions={
          <>
            <Button variant="outline" onClick={() => window.location.reload()}>
              <RotateCcw aria-hidden />
              Reload
            </Button>
            <Button asChild variant="ghost">
              <a href="/dashboard">Back to dashboard</a>
            </Button>
          </>
        }
      />
    </div>
  );
}

function PageSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading" />
    </div>
  );
}

export const router = createBrowserRouter([
  {
    element: <Root />,
    errorElement: (
      <ErrorBoundary>
        <RouteError />
      </ErrorBoundary>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      {
        path: 'login',
        // FIX(admin-route-error-element): the lazy login chunk had no Suspense
        // boundary of its own (AppShell's only wraps the shell routes), so while
        // it loaded React suspended up to the root. Before: bare <LoginPage />.
        // After: wrapped with a spinner fallback.
        element: (
          <Suspense fallback={<PageSpinner />}>
            <LoginPage />
          </Suspense>
        ),
      },
      {
        element: <AppShell />,
        children: [
          ...dashboardRoutes,
          ...analyticsRoutes,
          ...workspaceRoutes,
          ...floorRoutes,
          ...userRoutes,
          ...roleRoutes,
          ...projectRoutes,
          ...roomRoutes,
          ...meetingRoutes,
          ...recordingRoutes,
          ...taskRoutes,
          ...integrationRoutes,
          ...providerRoutes,
          ...billingRoutes,
          ...auditRoutes,
          ...attendanceRoutes,
          ...clientPolicyRoutes,
          ...securityRoutes,
          ...errorRoutes,
          ...notificationRoutes,
          ...filesRoutes,
          ...releaseRoutes,
          ...systemRoutes,
          ...messagingRoutes,
          ...docsRoutes,
          ...settingsRoutes,
          ...agentRoutes,
          ...aiOversightRoutes,
          { path: '*', element: <NotFound /> },
        ],
      },
    ],
  },
]);
